import { useElementDistribution } from '../../hooks/useElementDistribution';
import { publicationStateForSave } from '../../utils/formPublication';
import { useOnboardingDriver } from '../onboarding/GuidedFormCreation/OnboardingDriverContext';
import { useState, useEffect, useRef } from 'react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { QRCodeSVG } from 'qrcode.react';
import { getAuthToken, getAuthGroups } from '../../utils/auth';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { createRoot } from 'react-dom/client';
import { useLatestCallback } from '../../hooks/useLatestCallback';
import ElementEditorSection, {
  elementEditorSectionHasErrors,
  elementEditorSectionIcons,
  focusFirstInvalidElement,
} from '../workspaces/ElementEditorSection';
import '../../css/multi-select-styling.css';
import { setContrast, safeRemoveNode } from '@charitystack/shared';

// Import tab components
import QRCodeConfiguration from './tabs/QRCodeConfiguration';
import AppearanceTab from './tabs/AppearanceTab';

import type {
  QRCodeFormData,
  CreateQRCodeDrawerProps,
  Tab,
  WorkspaceHeaderAction,
} from './types/QRCodeTypes';
import type { FormData, ColorPickerColor } from './tabs/types';
import { portalClient } from '../../lib/trpc/portal';

// Utility function for classNames
function classNames(...classes: string[]): string {
  return classes.filter(Boolean).join(' ');
}

const tabs: Tab[] = [
  { name: 'Configuration', step: 'configuration', current: true },
  { name: 'Appearance', step: 'appearance', current: false },
];

const initialFormData: QRCodeFormData = {
  elementName: '',
  redirectType: 'HOSTED_PAGE',
  formIDs: [],
  connectedForms: [],
  customUrl: '',
  color: '',
};

export default function CreateQRCodeDrawer(props: CreateQRCodeDrawerProps) {
  const paymentsReady = useOnboardingDriver()?.chargesEnabled === true;
  const useCreateWorkspaceCards = props.presentation === 'workspace';
  const [formData, setFormData] = useState<QRCodeFormData>(initialFormData);
  const [elementID, setElementID] = useState<string | null>(null);
  const [availableForms, setAvailableForms] = useState<FormData[]>([]);
  const [active, setActive] = useState<boolean>(true);
  const { canDistribute, refresh: verifyDistribution } = useElementDistribution(
    {
      ...props.elementData,
      elementID,
      active,
      elementType: 'QR_CODE',
      QRCodeType: formData.redirectType,
      formIDs: [
        ...(props.elementData?.formIDs || []),
        ...formData.connectedForms
          .map((name) => availableForms.find((form) => form.formName === name)?.formID)
          .filter((id): id is string => !!id),
      ],
    },
    paymentsReady,
    props.open,
  );

  // Add validation states
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [currentStep, setCurrentStep] = useState<string>('configuration');

  // Add loading states
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isActivating, setIsActivating] = useState<boolean>(false);

  const [dataForQRCode, setDataForQRCode] = useState<string>('');

  // Add state for QR code download/copy operations
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isCopying, setIsCopying] = useState<boolean>(false);

  // Color picker state
  const [sketchPickerColor, setSketchPickerColor] = useState<ColorPickerColor>({
    r: 0,
    g: 0,
    b: 0,
    a: 1,
  });
  const [displayColorPicker, setDisplayColorPicker] = useState<boolean>(false);

  // QR Code ref for download/copy functionality
  const qrRef = useRef<HTMLDivElement>(null);

  // Add form reference
  const formRef = useRef<HTMLFormElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);

  // Update formData helper
  const updateFormData = (field: keyof QRCodeFormData, value: any): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle field focus to clear validation errors
  const handleFieldFocus = (fieldName: string) => {
    setInvalidFields((prev) => {
      const newInvalidFields = new Set(prev);
      newInvalidFields.delete(fieldName);
      const nonPrefixedName = fieldName.includes('-')
        ? fieldName.split('-').slice(1).join('-')
        : fieldName;
      newInvalidFields.delete(nonPrefixedName);
      return newInvalidFields;
    });
  };

  // Function to check if a field is invalid
  const isFieldInvalid = (fieldName: string) => {
    if (!invalidFields) return false;
    if (invalidFields instanceof Set) {
      if (invalidFields.has(fieldName)) {
        return true;
      }
      for (const tab of tabs) {
        if (invalidFields.has(`${tab.step}-${fieldName}`)) {
          return true;
        }
      }
      return false;
    }
    return false;
  };

  useEffect(() => {
    // Generate QR code data inside the effect so the function isn't recreated on every render
    const generateQRCodeData = () => {
      // You can set any data you want to encode in the QR code based on form input values
      const baseURL =
        import.meta.env.VITE_APP_ENV === 'development'
          ? 'https://dev-link.charitystack.com'
          : 'https://link.charitystack.com';
      const qrCodeURL = `${baseURL}/elements-config?elementID=${elementID}`;
      setDataForQRCode(canDistribute ? qrCodeURL : 'Preview only');
    };

    generateQRCodeData();
  }, [elementID, canDistribute]);

  // Handle form submission
  const handleSubmit = async (): Promise<void> => {
    const newInvalidFields = new Set<string>();

    if (!formData.elementName.trim()) {
      newInvalidFields.add('configuration-elementName');
    }
    if (
      formData.redirectType === 'HOSTED_PAGE' &&
      (!formData.connectedForms || formData.connectedForms.length === 0)
    ) {
      newInvalidFields.add('configuration-connectedForms');
    }
    if (formData.redirectType === 'CUSTOM_PAGE') {
      let url = formData.customUrl?.trim();
      if (!url) {
        newInvalidFields.add('configuration-customUrl');
      } else {
        // Auto-prepend https:// if no protocol is provided
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = `https://${url}`;
          updateFormData('customUrl', url);
        }
        // Validate URL format
        try {
          const parsedUrl = new URL(url);
          if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
            newInvalidFields.add('configuration-customUrl');
          } else if (!parsedUrl.hostname.includes('.') || parsedUrl.hostname.endsWith('.')) {
            // Ensure hostname has a valid domain (e.g., .com, .org, etc.)
            newInvalidFields.add('configuration-customUrl');
          }
        } catch {
          newInvalidFields.add('configuration-customUrl');
        }
      }
    }
    if (!formData.color || !formData.color.trim()) {
      newInvalidFields.add('appearance-color');
    }

    if (newInvalidFields.size > 0) {
      setInvalidFields(newInvalidFields);
      focusFirstInvalidElement(rightPanelRef.current);
      toast.error('Please fix validation errors');
      return;
    }

    setIsSaving(true);

    try {
      const payload: any = {
        nonprofitName: props.merchant?.merchantName || 'Unknown Merchant',
        CSMerchantID: props.merchant?.organizationId || '',
        color: formData.color,
        elementName: formData.elementName,
        elementType: 'QR_CODE',
        QRCodeType: formData.redirectType,
        qrCodeType: formData.redirectType,
        formNames: formData.redirectType === 'HOSTED_PAGE' ? formData.connectedForms : ['-'],
        formIDs:
          formData.redirectType === 'HOSTED_PAGE'
            ? formData.connectedForms
                .map(
                  (formName) => availableForms.find((form) => form.formName === formName)?.formID,
                )
                .filter((id) => id !== undefined)
            : ['-'],
        customUrl: formData.redirectType === 'CUSTOM_PAGE' ? formData.customUrl : '',
        active: publicationStateForSave({ existingId: elementID, active, paymentsReady }),
        ...(!elementID && {
          timeCreated: new Date().toISOString(),
          createdBy: props.user?.email || 'unknown',
        }),
      };

      if (elementID) {
        payload.elementID = elementID;
        delete payload.active;
      }

      const method = elementID ? 'PATCH' : 'POST';
      const url = `${import.meta.env.VITE_APP_ELEMENTS_URL}/elements`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: await getAuthToken(),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        try {
          const data = await response.json();
          toast.error(data.error);
        } catch {
          throw new Error('API request failed');
        }
        return;
      }

      const data = await response.json();

      toast.success(
        elementID
          ? 'Element updated successfully'
          : paymentsReady
            ? 'Element published successfully'
            : 'Draft saved successfully',
      );
      if (!elementID) {
        setActive(data.active ?? payload.active);
        setElementID(data.elementID);
      }
      if (props.onSuccess) props.onSuccess(data);
    } catch (error: any) {
      console.error('Submission error:', error);
      toast.error(`Failed to ${elementID ? 'update' : 'publish'} element`, {
        description: error?.message || 'Please try again',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Activate/Deactivate element
  const activateElement = async (): Promise<void> => {
    setIsActivating(true);
    const state = !active;
    if (state && !paymentsReady) {
      setIsActivating(false);
      toast.info('Enable payments before publishing this draft.');
      return;
    }
    const successAlertMessage = state ? 'Element Activated' : 'Element Deactivated';

    try {
      const payload = {
        elementID,
        nonprofitName: props.merchant?.merchantName || 'Unknown Merchant',
        active: state,
      };

      const requestOptions = {
        method: 'PATCH',
        headers: {
          'content-type': 'application/json',
          Authorization: await getAuthToken(),
        },
        body: JSON.stringify(payload),
      };

      const url = `${import.meta.env.VITE_APP_ELEMENTS_URL}/elements/toggle-active`;
      const response = await fetch(url, requestOptions);
      const data = await response.json();

      if (data.error) {
        toast.error(data.error);
        return;
      } else {
        toast.success(successAlertMessage);
        setActive(state);
        if (props.onSuccess) props.onSuccess(data);
      }
    } catch (error) {
      console.error('Activation error:', error);
      toast.error('Failed to update element status');
    } finally {
      setIsActivating(false);
    }
  };

  // Copy QR code image to clipboard
  const copyToClipboard = async (): Promise<void> => {
    if (!canDistribute || !(await verifyDistribution())) return;
    try {
      setIsCopying(true);

      // Create a temporary container off-screen with inline styles for reliable capture
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      document.body.appendChild(tempContainer);

      const root = createRoot(tempContainer);
      let captureRef: HTMLDivElement | null = null;

      root.render(
        <div
          ref={(ref) => {
            captureRef = ref;
          }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            border: '1px solid #E0D9CC',
            backgroundColor: '#ffffff',
            padding: '8px',
          }}
        >
          <div style={{ paddingTop: '24px', paddingBottom: '40px', display: 'inline-block' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <QRCodeSVG
                value={dataForQRCode}
                size={200}
                bgColor="#ffffff"
                fgColor="#000000"
                level="M"
                includeMargin={false}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: '-36px',
                  left: 0,
                  right: 0,
                  borderRadius: '0 0 8px 8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  paddingBottom: '16px',
                  backgroundColor: effectiveColor,
                }}
              >
                <p
                  style={{
                    wordBreak: 'break-word',
                    fontSize: '14px',
                    fontWeight: '600',
                    textAlign: 'center',
                    color: setContrast(effectiveColor),
                    margin: 0,
                  }}
                >
                  Donate Now
                </p>
              </div>
            </div>
          </div>
        </div>,
      );

      // Wait for rendering using double-rAF to ensure paint is complete
      requestAnimationFrame(() => {
        requestAnimationFrame(async () => {
          if (captureRef) {
            try {
              window.focus();
              const canvas = await html2canvas(captureRef, {
                backgroundColor: '#ffffff',
                scale: 3,
                useCORS: true,
                allowTaint: false,
              });

              canvas.toBlob(async (blob) => {
                if (!blob) {
                  setIsCopying(false);
                  toast.error('Failed to generate image');
                  root.unmount();
                  safeRemoveNode(tempContainer);
                  return;
                }

                try {
                  // Check if Clipboard API is available
                  if (!navigator?.clipboard?.write) {
                    toast.error('Clipboard API not available in this context');
                    setIsCopying(false);
                    root.unmount();
                    safeRemoveNode(tempContainer);
                    return;
                  }

                  await navigator.clipboard.write([
                    new ClipboardItem({
                      'image/png': blob,
                    }),
                  ]);
                  setIsCopying(false);
                  toast.success('QR Code image copied to clipboard!');
                } catch (err) {
                  console.error('Failed to copy image: ', err);
                  setIsCopying(false);
                  toast.error('Failed to copy image. Please try downloading instead.');
                } finally {
                  root.unmount();
                  safeRemoveNode(tempContainer);
                }
              }, 'image/png');
            } catch (err) {
              console.error('Failed to generate image: ', err);
              setIsCopying(false);
              toast.error('Failed to generate QR code image');
              root.unmount();
              safeRemoveNode(tempContainer);
            }
          }
        });
      });
    } catch (err) {
      console.error('Failed to initialize QR code: ', err);
      setIsCopying(false);
      toast.error('Failed to generate QR Code image');
    }
  };

  // Download QR code image
  const downloadImage = async (): Promise<void> => {
    if (!canDistribute || !(await verifyDistribution())) return;
    try {
      setIsDownloading(true);

      // Create a temporary container off-screen with inline styles for reliable capture
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      document.body.appendChild(tempContainer);

      const root = createRoot(tempContainer);
      let captureRef: HTMLDivElement | null = null;

      root.render(
        <div
          ref={(ref) => {
            captureRef = ref;
          }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            border: '1px solid #E0D9CC',
            backgroundColor: '#ffffff',
            padding: '8px',
          }}
        >
          <div style={{ paddingTop: '24px', paddingBottom: '40px', display: 'inline-block' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <QRCodeSVG
                value={dataForQRCode}
                size={200}
                bgColor="#ffffff"
                fgColor="#000000"
                level="M"
                includeMargin={false}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: '-36px',
                  left: 0,
                  right: 0,
                  borderRadius: '0 0 8px 8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  paddingBottom: '16px',
                  backgroundColor: effectiveColor,
                }}
              >
                <p
                  style={{
                    wordBreak: 'break-word',
                    fontSize: '14px',
                    fontWeight: '600',
                    textAlign: 'center',
                    color: setContrast(effectiveColor),
                    margin: 0,
                  }}
                >
                  Donate Now
                </p>
              </div>
            </div>
          </div>
        </div>,
      );

      // Wait for rendering using double-rAF to ensure paint is complete
      requestAnimationFrame(() => {
        requestAnimationFrame(async () => {
          if (captureRef) {
            try {
              const canvas = await html2canvas(captureRef, {
                backgroundColor: '#ffffff',
                scale: 3,
                useCORS: true,
                allowTaint: false,
              });

              const link = document.createElement('a');
              const baseFilename = formData.elementName
                ? formData.elementName
                    .toLowerCase()
                    .replace(/[^a-z0-9]/g, '-')
                    .replace(/-+/g, '-')
                    .substring(0, 30)
                : 'qr-code';
              link.download = `${baseFilename}-qr-code.png`;
              link.href = canvas.toDataURL();
              link.click();

              setIsDownloading(false);
              toast.success('QR Code downloaded successfully!');
              root.unmount();
              safeRemoveNode(tempContainer);
            } catch (err) {
              console.error('Failed to download QR code: ', err);
              setIsDownloading(false);
              toast.error('Failed to download QR code');
              root.unmount();
              safeRemoveNode(tempContainer);
            }
          }
        });
      });
    } catch (err) {
      console.error('Failed to initialize QR code: ', err);
      setIsDownloading(false);
      toast.error('Failed to generate QR Code image');
    }
  };

  // Fetch forms
  useEffect(() => {
    const fetchForms = async (): Promise<void> => {
      if (!props.open) return;

      try {
        const merchants = await getAuthGroups();

        const response = await portalClient.postFormAndFundData({
          authorization: await getAuthToken(),
          body: {
            queryStringParameters: {
              nonprofit: merchants[0],
            },
          },
        });

        const data = await response.json();
        setAvailableForms(data.formNames);
      } catch (error) {
        console.error('Error fetching forms:', error);
        setAvailableForms([]);
      }
    };

    fetchForms();
  }, [props.open]);

  // Populate form data for edit mode
  useEffect(() => {
    if (props.elementData && props.open) {
      const data = props.elementData;

      setElementID(data.elementID);
      setFormData({
        elementName: data.elementName,
        redirectType: data.QRCodeType || 'HOSTED_PAGE',
        connectedForms: data.formNames || [],
        formIDs: data.formIDs || [],
        customUrl: data.QRCodeType === 'CUSTOM_PAGE' ? data.destinationURL : '',
        color: data.color,
      });
      setActive(data.active);
      setCurrentStep('configuration');
    }
  }, [props.elementData, props.open]);

  // Pre-fill the element color from the org's brand color when the drawer opens.
  // Only applies when (a) the drawer is open, (b) the org has a brand color set, and
  // (c) the color field is still untouched (empty) — so a color the user has picked
  // or one loaded from an existing element in edit mode is never overwritten.
  useEffect(() => {
    if (!props.open) return;
    const orgBrandColor = props.merchant?.brandColor;
    if (!orgBrandColor) return;
    setFormData((prev) => {
      if (prev.color) return prev; // user picked one, or edit-mode loaded one
      return { ...prev, color: orgBrandColor };
    });
  }, [props.open, props.merchant?.brandColor]);

  const clearStates = (): void => {
    setFormData(initialFormData);
    setElementID(null);
    setAvailableForms([]);
    setInvalidFields(new Set());
    setCurrentStep('configuration');
    setSketchPickerColor({ r: 0, g: 0, b: 0, a: 1 });
    setDisplayColorPicker(false);
    setActive(true);
    setIsSaving(false);
    setIsActivating(false);
    setIsDownloading(false);
    setIsCopying(false);

    if (formRef.current) formRef.current.reset();
  };

  const handleSubmitLatest = useLatestCallback(handleSubmit);
  const activateElementLatest = useLatestCallback(activateElement);

  useEffect(() => {
    if (props.presentation !== 'workspace' || !props.setWorkspaceActions) return undefined;

    const actions: WorkspaceHeaderAction[] = [];

    if (elementID) {
      actions.push(
        {
          key: 'save',
          label: isSaving ? 'Saving...' : 'Save',
          variant: 'primary' as const,
          disabled: isSaving || isActivating,
          onClick: () => handleSubmitLatest(),
        },
        {
          key: 'active',
          label: !active
            ? isActivating
              ? 'Activating...'
              : 'Activate'
            : isActivating
              ? 'Deactivating...'
              : 'Deactivate',
          disabled: isActivating || isSaving || (!active && !paymentsReady),
          variant: !active ? 'primary' : 'danger',
          onClick: () => activateElementLatest(),
        },
      );
    } else {
      actions.push({
        key: 'publish',
        label: isSaving ? 'Saving...' : paymentsReady ? 'Publish' : 'Save draft',
        disabled: isSaving || isActivating,
        variant: 'primary' as const,
        onClick: () => handleSubmitLatest(),
      });
    }

    props.setWorkspaceActions(actions);
    return () => props.setWorkspaceActions?.(null);
    // Register current button state without depending on recreated handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.presentation,
    props.setWorkspaceActions,
    isSaving,
    isActivating,
    elementID,
    active,
    paymentsReady,
    handleSubmitLatest,
    activateElementLatest,
  ]);

  // Don't render if required props are missing
  if (!props.merchant) {
    return null;
  }

  const effectiveColor = formData.color || '#9A9285';

  return (
    <Sheet
      embedded={props.presentation === 'workspace'}
      open={props.open}
      onOpenChange={(open) => {
        props.setOpen(open);
        if (!open) clearStates();
      }}
    >
      <SheetContent
        embedded={props.presentation === 'workspace'}
        side="bottom"
        className={
          props.presentation === 'workspace'
            ? 'min-h-full overflow-visible rounded-none bg-white p-0'
            : 'pointer-events-none h-[95vh] overflow-hidden rounded-t-[10px] bg-white'
        }
        onEscapeKeyDown={() => clearStates()}
        onPointerDownOutside={() => clearStates()}
      >
        {/* Main Container - centered */}
        <div
          className={`mx-auto w-full max-w-[1600px] ${
            props.presentation === 'workspace' ? 'px-3 py-2 md:py-3' : 'h-full pointer-events-none'
          }`}
        >
          {/* Header with Action Buttons */}
          {props.presentation !== 'workspace' && (
            <div className="pointer-events-auto mb-4 flex items-center justify-between rounded-lg bg-white p-0">
              <div className="flex items-start gap-x-[6px]">
                <div className="flex h-[38px] w-[38px] items-center justify-center rounded-[19px] border border-edge bg-paper-inset">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="h-6 w-6"
                  >
                    <path
                      d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm13 0h3v3h3v3h-3v3h-3v-3h-3v-3h3v-3zm0-2h-3v3h3v-3z"
                      fill="#6B6458"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-base text-ink-muted">
                    {elementID ? 'Edit QR Code' : 'Create QR Code'}
                  </p>
                  <p className="text-sm text-ink-muted">
                    Generate QR codes for easy donation access
                  </p>
                </div>
              </div>

              <div className="flex flex-row items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    props.setOpen(false);
                    clearStates();
                  }}
                >
                  Close
                </Button>

                {/* Save Button - Only when editing */}
                {elementID && (
                  <Button
                    onClick={() => handleSubmit()}
                    disabled={isSaving || isActivating}
                    variant="primary"
                    size="workspace"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                )}

                {/* Activate/Deactivate Button - Only when editing */}
                {elementID && (
                  <Button
                    onClick={() => activateElement()}
                    disabled={isActivating || isSaving || (!active && !paymentsReady)}
                    variant={!active ? 'primary' : 'danger'}
                    size="workspace"
                  >
                    {!active
                      ? isActivating
                        ? 'Activating...'
                        : 'Activate'
                      : isActivating
                        ? 'Deactivating...'
                        : 'Deactivate'}
                  </Button>
                )}

                {/* Publish Button - Only when creating */}
                {!elementID && (
                  <Button
                    onClick={() => handleSubmit()}
                    variant="primary"
                    size="workspace"
                    disabled={isSaving || isActivating}
                  >
                    {isSaving ? 'Saving...' : paymentsReady ? 'Publish' : 'Save draft'}
                  </Button>
                )}
              </div>
            </div>
          )}

          {props.presentation !== 'workspace' && (
            <div className="pb-3">
              <Separator className="bg-paper-inset" />
            </div>
          )}

          {/* Main Content - Split Layout */}
          <div
            className={`pointer-events-auto flex w-full flex-col-reverse gap-6 lg:flex-row ${
              props.presentation === 'workspace'
                ? 'overflow-visible'
                : 'h-[calc(100%-80px)] overflow-y-auto md:overflow-hidden'
            }`}
          >
            {/* Left Side - Preview Section */}
            <div
              className={`flex flex-col gap-4 pr-2 md:max-h-[250px] lg:w-1/2
              ${useCreateWorkspaceCards ? 'overflow-visible' : 'h-full overflow-y-auto md:min-h-full'}
              ${elementID ? 'min-h-[400px] max-h-[450px]' : 'min-h-[200px] max-h-[250px]'}
              `}
            >
              {elementID ? (
                <>
                  <div className="flex w-full flex-col items-stretch rounded-md border border-edge bg-paper-muted px-4 py-4">
                    {!canDistribute && (
                      <p className="mb-3 text-center text-sm text-ink-muted">
                        Preview only — no shareable destination yet.
                      </p>
                    )}
                    <div
                      ref={qrRef}
                      className="flex w-full flex-col items-center justify-center rounded-lg border border-edge bg-white p-2"
                    >
                      <div className="inline-block pt-6 pb-10">
                        <div className="relative inline-block">
                          <QRCodeSVG
                            value={dataForQRCode}
                            size={200}
                            bgColor="#ffffff"
                            fgColor="#000000"
                            level="M"
                            includeMargin={false}
                          />
                          {/* Bottom colored bar */}
                          <div
                            className="absolute bottom-[-38px] left-0 right-0 rounded-b-lg shadow-md p-2"
                            style={{
                              backgroundColor: effectiveColor,
                              color: setContrast(effectiveColor),
                            }}
                          >
                            <p
                              className="break-words text-sm font-semibold text-center"
                              style={{
                                color: setContrast(effectiveColor),
                              }}
                            >
                              Donate Now
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex gap-3">
                    <Button
                      variant="neutral"
                      size="workspace"
                      onClick={copyToClipboard}
                      disabled={isCopying || isDownloading || !canDistribute}
                      className="flex items-center gap-2"
                    >
                      <svg
                        width="18"
                        height="19"
                        viewBox="0 0 18 19"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M7.875 3.19409C7.36058 3.19409 6.92546 3.53984 6.79207 4.01271C6.76478 4.10947 6.75 4.21209 6.75 4.31909H11.25C11.25 4.21209 11.2352 4.10947 11.2079 4.01271C11.0745 3.53984 10.6394 3.19409 10.125 3.19409H7.875ZM5.8549 3.32751C6.22133 2.58236 6.98769 2.06909 7.875 2.06909H10.125C11.0123 2.06909 11.7787 2.58236 12.1451 3.32751C12.5183 3.35931 12.8899 3.39672 13.2598 3.43965C14.3829 3.56998 15.1875 4.53451 15.1875 5.63683V15.5691C15.1875 16.8117 14.1801 17.8191 12.9375 17.8191H5.0625C3.81986 17.8191 2.8125 16.8117 2.8125 15.5691V5.63683C2.8125 4.53451 3.61712 3.56998 4.74017 3.43965C5.1101 3.39672 5.48171 3.35931 5.8549 3.32751Z"
                          fill="currentColor"
                        />
                      </svg>
                      {isCopying ? 'Copying...' : 'Copy Image'}
                    </Button>
                    <Button
                      onClick={downloadImage}
                      disabled={isCopying || isDownloading || !canDistribute}
                      variant="neutral"
                      size="workspace"
                      className="flex items-center gap-2"
                    >
                      {isDownloading ? 'Downloading...' : 'Download'}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex w-full flex-col items-center justify-center rounded-md border border-edge bg-paper-muted px-4 py-12">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="h-16 w-16 text-ink-muted mb-4"
                  >
                    <path
                      d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm13 0h3v3h3v3h-3v3h-3v-3h-3v-3h3v-3zm0-2h-3v3h3v-3z"
                      fill="currentColor"
                    />
                  </svg>
                  <p className="text-center text-sm text-ink-muted font-medium mb-1">
                    QR Code Preview
                  </p>
                  <p className="text-center text-xs text-ink-muted">
                    Publish your QR code to see the preview and download options
                  </p>
                </div>
              )}
            </div>

            {/* Right Side - Form Configuration */}
            <div
              ref={rightPanelRef}
              className={`compact-editor-controls flex flex-col lg:w-1/2 lg:pl-4 ${
                useCreateWorkspaceCards
                  ? 'min-h-0 overflow-visible pb-0'
                  : 'min-h-[320px] overflow-hidden pb-14 md:min-h-full md:pb-0'
              }`}
            >
              {/* Form Tabs */}
              {!useCreateWorkspaceCards && (
                <div className="border-edge bg-white">
                  <nav
                    className="-mb-px flex space-x-6 overflow-x-auto border-b pb-2 md:overflow-x-hidden md:pb-0 pt-2"
                    aria-label="Tabs"
                  >
                    {tabs.map((tab) => {
                      const tabHasErrors = Array.from(invalidFields).some((field) =>
                        field.startsWith(tab.step + '-'),
                      );
                      return (
                        <button
                          type="button"
                          key={tab.name}
                          onClick={() => setCurrentStep(tab.step)}
                          className={classNames(
                            currentStep === tab.step
                              ? tabHasErrors
                                ? 'border-red-500 text-red-600'
                                : 'border-ink-secondary text-ink-secondary'
                              : tabHasErrors
                                ? 'border-transparent text-red-500 hover:text-red-600'
                                : 'border-transparent text-ink-muted hover:text-ink-secondary',
                            'whitespace-nowrap px-1 pb-1.5 text-[13px] font-medium border-b-2 border-x-0 border-t-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
                          )}
                        >
                          {tab.name}
                        </button>
                      );
                    })}
                  </nav>
                </div>
              )}

              {/* Form Content */}
              <div
                className={`flex-1 ${
                  useCreateWorkspaceCards ? 'space-y-4 overflow-visible' : 'overflow-y-auto py-4'
                }`}
              >
                {/* Configuration Section */}
                <div
                  className={
                    useCreateWorkspaceCards || currentStep === 'configuration'
                      ? useCreateWorkspaceCards
                        ? ''
                        : 'space-y-2'
                      : 'hidden'
                  }
                >
                  <ElementEditorSection
                    enabled={useCreateWorkspaceCards}
                    title="Configuration"
                    description="Choose the destination and connected form for this QR code."
                    icon={elementEditorSectionIcons.configuration}
                    hasErrors={elementEditorSectionHasErrors(invalidFields, 'configuration')}
                  >
                    <QRCodeConfiguration
                      formData={formData}
                      updateFormData={updateFormData}
                      availableForms={availableForms}
                      handleFieldFocus={handleFieldFocus}
                      isFieldInvalid={isFieldInvalid}
                      invalidFields={invalidFields}
                      setInvalidFields={setInvalidFields}
                    />
                  </ElementEditorSection>
                </div>

                {/* Appearance Section */}
                <div
                  className={
                    useCreateWorkspaceCards || currentStep === 'appearance'
                      ? useCreateWorkspaceCards
                        ? ''
                        : 'space-y-4'
                      : 'hidden'
                  }
                >
                  <ElementEditorSection
                    enabled={useCreateWorkspaceCards}
                    title="Appearance"
                    description="Set the QR code color and visual treatment."
                    icon={elementEditorSectionIcons.appearance}
                    hasErrors={elementEditorSectionHasErrors(invalidFields, 'appearance')}
                  >
                    <AppearanceTab
                      color={formData.color}
                      setColor={(value) => updateFormData('color', value)}
                      sketchPickerColor={sketchPickerColor}
                      setSketchPickerColor={setSketchPickerColor}
                      displayColorPicker={displayColorPicker}
                      setDisplayColorPicker={setDisplayColorPicker}
                      handleFieldFocus={handleFieldFocus}
                      isFieldInvalid={isFieldInvalid}
                      invalidFields={invalidFields}
                      setInvalidFields={setInvalidFields}
                      elementType="qr-code"
                    />
                  </ElementEditorSection>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
